/* ==========================================================================
   Elle Exist — collection page (PLP)
   --------------------------------------------------------------------------
   The theme opens the filter panel as a drawer only below 768px; above that it
   is an always-visible sidebar and the toggle does nothing but trap focus.
   This design has one left drawer at every width, so the open/close state is
   driven from here and the theme's own handlers are left to do the rest
   (focus trap on open, class removal from its close buttons).

   Also keeps the drawer's "View N items" button in step with the product count
   the theme refreshes on every filter change.
   ========================================================================== */

(function () {
  'use strict';

  var SECTION = '.ee-plp';
  var VISIBLE = 'collection__filters--visible';
  var OPEN = 'ee-plp--filters-open';
  var UNLOCK_DELAY = 300;

  function init(section) {
    if (!section || section.dataset.eePlpBound === 'true') return;

    var filters = section.querySelector('[data-collection-filters]');
    if (!filters) return;

    section.dataset.eePlpBound = 'true';

    var scrim = section.querySelector('[data-ee-filters-scrim]');
    var toggle = section.querySelector('[data-toggle-filters]');
    var count = section.querySelector('[data-products-count]');
    var scroller = filters.querySelector('[data-collection-filters-list]');
    var locked = false;

    /* What the shopper asked for. The theme re-adds the visible class after
       every filter or sort render below 768px (mobileFiltersScrollLock), on
       the assumption that the shopper is standing inside the drawer — true in
       the stock layout, where sorting lives in there. Here sorting is in the
       toolbar, so choosing a sort was opening a drawer nobody asked for.
       This is the authority now, and sync() corrects the DOM to match. */
    var wanted = filters.classList.contains(VISIBLE);

    function isOpen() {
      return filters.classList.contains(VISIBLE);
    }

    function setOpen(next) {
      filters.classList.toggle(VISIBLE, next);
    }

    /* Capture on the section fires before any listener on the button itself,
       so the state read here is the state before the theme's handler flips
       it — whichever order the two were bound in. */
    section.addEventListener(
      'click',
      function (event) {
        var target = event.target;
        if (!target || !target.closest) return;
        if (!target.closest('[data-toggle-filters]')) return;

        wanted = !isOpen();
        var next = wanted;
        requestAnimationFrame(function () {
          setOpen(next);
        });
      },
      true
    );

    /* The theme's own close controls only strip the class; the intent has to
       be recorded here too, or sync() would put the drawer straight back. */
    section.addEventListener(
      'click',
      function (event) {
        var target = event.target;
        if (!target || !target.closest) return;
        if (target.closest('[data-close-filters]')) wanted = false;
      },
      true
    );

    if (scrim) {
      scrim.addEventListener('click', function () {
        wanted = false;
        setOpen(false);
      });
    }

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && isOpen()) {
        wanted = false;
        setOpen(false);
      }
    });

    /* The theme's lock handler defers its work by a task and clears any
       pending unlock timer on the way in:

         lock(e)   { setTimeout(() => { clearTimeout(unlockTimer); disable(); }) }
         unlock(e) { unlockTimer = setTimeout(remove, e.detail) }

       A MutationObserver callback is a microtask, so an unlock sent from
       sync() is registered before that deferred lock runs — and is then
       cancelled by it. The page ends up locked with nothing pending to
       release it. This one is queued after the theme's timer and asks for
       immediate removal, so there is no timer left for a later lock to clear. */
    function forceUnlock() {
      setTimeout(function () {
        locked = false;
        document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
      }, 0);
    }

    function setLock(next) {
      if (next === locked) return;
      locked = next;

      document.dispatchEvent(
        new CustomEvent(next ? 'theme:scroll:lock' : 'theme:scroll:unlock', {
          bubbles: true,
          detail: next ? scroller : UNLOCK_DELAY,
        })
      );
    }

    /* The theme's close buttons only strip the class, so the page-level state
       follows the drawer rather than the other way round. */
    function sync() {
      /* Correct the drawer rather than follow it: whoever changed that class,
         the shopper's own last instruction wins. The observer fires again on
         the corrected class and the work below runs then. */
      if (isOpen() !== wanted) {
        setOpen(wanted);

        /* mobileFiltersScrollLock dispatches theme:scroll:lock on every
           filter or sort render below 768px — outside its own guard, so it
           fires whether or not the drawer was open. Closing the drawer again
           is not enough; the lock has to be released too. */
        if (!wanted) forceUnlock();

        return;
      }

      var open = isOpen();

      section.classList.toggle(OPEN, open);
      if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');

      setLock(open);
    }

    new MutationObserver(sync).observe(filters, {
      attributes: true,
      attributeFilter: ['class'],
    });

    /* "View N items". The count element survives a filter change (the theme
       swaps its innerHTML); the button does not, so it is re-resolved each
       time and the write is guarded to keep the observer from re-firing. */
    function relabel() {
      if (!count) return;

      var label = filters.querySelector('[data-ee-apply-label]');
      if (!label) return;

      var template = label.getAttribute('data-ee-apply-template');
      if (!template) return;

      var matched = (count.textContent || '').match(/\d[\d.,\s\u00a0]*/);
      if (!matched) return;

      var next = template.replace('[count]', matched[0].trim());
      if (label.textContent !== next) label.textContent = next;
    }

    if (count) {
      new MutationObserver(relabel).observe(count, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    new MutationObserver(relabel).observe(filters, {childList: true, subtree: true});

    relabel();
    sync();
  }

  function initAll(root) {
    (root || document).querySelectorAll(SECTION).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
